<template>
  <div class="row enrollment-container justify-content-center">
    <div v-if="loading" class="loading-screen">
      <span class="fa fa-sync anim-spin" />
      <h4 class="text-center">
        {{ $t('loading') }}...
      </h4>
    </div>
    <div class="col-lg-8">
      <b-modal
        v-if="showModal"
        v-model="showModal"
        :title="enrollmentState.instructions.title"
      >
        <div v-html="$sanitize(enrollmentState.instructions.text, { allowedTags: $sanitize.defaults.allowedTags.concat(['img', 's']), allowedAttributes: {'img': ['src', 'style'] } })" />
        <div slot="modal-footer" class="modal-footer">
          <button type="button" class="btn btn-primary" @click="showModal = false">
            {{ enrollmentState.instructions.dismissText }}
          </button>
        </div>
      </b-modal>
      <Progress
        v-if="enrollmentState && enrollmentState.progress"
        :studyId="studyId"
        :participantId="participantId"
        :enrollmentState="enrollmentState"
      />
      <component
        :is="currentStep"
        :key="stepState.id"
        :studyId="studyId"
        :participantId="participantId"
        :account="account"
        :initialStudy="initialStudy"
        :stepState="stepState"
        :enrollmentState="enrollmentState"
        @loading="setLoading"
        @ready="setReady"
        @refresh="refreshState"
      />
    </div>
  </div>
</template>

<script>
  import { BModal } from "bootstrap-vue";

  import Progress from './components/Progress.vue';
  import AccountCreation from './components/AccountCreation.vue';
  import ContactVerification from './components/ContactVerification.vue';
  import DeviceAuthorization from './components/DeviceAuthorization.vue';
  import Survey from './components/Survey.vue';
  import Message from './components/Message.vue';
  import WrongStudy from './components/WrongStudy.vue';
  import Profile from './components/Profile.vue';
  import W9 from './components/W9.vue';
  import LotterySelection from './components/LotterySelection.vue';
  import Redirect from './components/Redirect.vue';

  const stepMap = {
    'ContactVerification': ContactVerification,
    'DeviceAuthorization': DeviceAuthorization,
    'Message': Message,
    'Survey': Survey,
    'EnrollmentBlock': Message,
    'Profile': Profile,
    'W9': W9,
    'LotterySelection': LotterySelection,
    'Redirect': Redirect
  };

  export default {
    components: {
      BModal,
      Progress,
      AccountCreation,
      Survey,
      ContactVerification,
      Message,
      W9,
      LotterySelection,
      Redirect,
      WrongStudy
    },
    props: {
      'studyId': {
        type: Number,
        required: true,
      },
      'initialAccount': {
        // initialAccount is either:
        // - an account object,
        // - false ("there IS no account because they're logged out"),
        // - or null ("query the API to see if they're logged in")
        type: [Object, Boolean],
        default: null
      },
      'initialParticipantId': {
        type: [Number, Boolean],
        default: null
      },
      'initialStudy': {
        type: [Object, Boolean],
        default: null
      }
    },
    data() {
      return {
        loading: true,
        account: this.initialAccount,
        participantId: this.initialParticipantId,
        enrollmentState: null,
        showModal: false,
      };
    },
    computed: {
      // The component for the currently-available thing-to-do
      currentStep() {
        // we have an account but not in this study
        if (this.account && !this.participantId) {
          return WrongStudy;
        }

        // we have an account and a step, and a component to display for that step type
        if (this.currentStepType && stepMap.hasOwnProperty(this.currentStepType)) {
          return stepMap[this.currentStepType];
        }

        // if we know we aren't logged in, display account creation form
        if (this.account === false) {
          return AccountCreation;
        }

        return null;
      },
      currentStepType() {
        if (this.enrollmentState && this.enrollmentState.steps) {
          return this.enrollmentState.steps[0].type;
        }
        return false;
      },
      stepState() {
        if (this.enrollmentState && this.enrollmentState.steps) {
          return this.enrollmentState.steps[0]
        }
        return false;
      }
    },
    watch: {
      account(newValue) {
        if (this.$store) {
          this.$store.commit('SET_ACCOUNT', newValue);
        }
      },
      stepState(step) {
        if (step && this.$gtag) {
          let user_id = null;
          if(this.$store) {
            user_id = this.$store.getters.getParticipant ? this.$store.getters.getParticipant.id : null;
          }

          // send a new page view with the step state detail, since route didn't change to track it
          this.$gtag.pageview({
            page_title: this.$route.name,
            page_path: this.$route.path,
            page_location: document.location.href,
            user_id: user_id,
            screen_name: step.name || step.type
          });
        }
      }
    },
    created() {
      this.getAccount()
        .then(this.getEnrollmentState)
    },
    methods: {
      setLoading() {
        this.loading = true;
      },
      setReady() {
        this.loading = false;
        this.$emit('ready');
      },
      getAccount() {
          // returns Promise
        // if we already have an account set,
        // or it was set to false by initial prop, move along
        if (this.account !== null) {
          // immediately resolve
          return Promise.resolve();
        }

        // check the api to see if we have an account
        return this.$api.Authentication.me().then(({body}) => {
          if (!body.data) {
            this.account = false;
            return;
          }
          // we are logged in, set our account
          this.account = body.data;
          this.setParticipantId();
        }).catch(() => {
          // we got a 401, not logged in
          this.account = false;
        });
      },
      getEnrollmentState() { // returns Promise
        if (!this.participantId) {
          // no participant, not logged in, no state
          return Promise.resolve();
        }

        return this.$api.Participants.getEnrollmentStateForParticipant({
          participant_id: this.participantId
        }).then(({body, status}) => {
          if (status >= 400) {
            throw { status: status, body: body };
          }

          this.enrollmentState = body.data;
          if (this.enrollmentState && this.enrollmentState.instructions) {
              this.showModal = true;
          }
        }).catch((err) => {
          // TODO: figure out how to "log them out" if it was a 403 or something
        });
      },
      setParticipantId() {
        if (this.account && this.account.StudyUsers) {
          let su = this.account.StudyUsers.find((su) => {
            return su.study_id == this.studyId && su.role == 'Participant';
          });
          this.participantId = su ? su.study_user_id : false;
        }
      },
      refreshState() {
        // if we were initially logged out, clear this so
        // we can check if we've been logged in
        if (this.account === false) {
          this.account = null;
        }

        this.loading = true;
        this.getAccount()
          .then(this.getEnrollmentState);
      }
    }
  }
</script>

<style>
  .loading-screen {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: rgba(255, 255, 255, 0.7);
    text-align: center;
    padding-top: 10vh;
    z-index: 9999;
  }

  .loading-screen .glyphicon {
    font-size: 5em;
  }
</style>
